<script lang="ts" setup>
import { computed, ref, nextTick, watch, onUnmounted } from 'vue'
import { computePosition, flip, shift, offset, arrow } from '@floating-ui/dom'
import {
  FileText,
  ChevronDown,
  Send,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
  ExternalLink
} from 'lucide-vue-next'
import Button from '@/components/ui/button/Button.vue'
import CookingPotEditorSkeleton from '@/feature/CookingPot/components/Editor/CookingPotEditorSkeleton.vue'
import AdminDebugStepsPanel from '@/components/customUi/AdminDebugStepsPanel.vue'
import ResearchChatCitationCard from './ResearchChatCitationCard.vue'
import ResearchCitationActionDialog from './ResearchCitationActionDialog.vue'
import type { ChatMessage, Citation } from './useResearchChat'
import { useMarkdown } from './useMarkdown'
import { navigateTo } from '@/router/utils'
import { Routes } from '@/router/routes'
import { useTranslateKey } from '@/lib/utils'
import { storeEditorPrefill } from '@/lib/editorPrefillTransport'
import { useUserStore } from '@/stores/userStore'

const props = withDefaults(
  defineProps<{
    message: ChatMessage
    compact?: boolean
    showActions?: boolean
    showSendToEditor?: boolean
    canRetry?: boolean
    retryDisabled?: boolean
    showFeedback?: boolean
    showAvatar?: boolean
    draftOpen?: boolean
  }>(),
  {
    showActions: true,
    showSendToEditor: true,
    canRetry: false,
    retryDisabled: false,
    showFeedback: true,
    showAvatar: false,
    draftOpen: false
  }
)

const emit = defineEmits<{
  sendToEditor: [content: string]
  retry: [messageId: string]
  feedback: [payload: { messageId: string; value: 'like' | 'dislike' | null }]
  openDraft: [payload: { workSessionUuid: string; entryId?: string }]
}>()

// A generated draft (reward) attached to this message — shown as an artifact the user can toggle
// open in the side-by-side editor.
const hasDraft = computed(
  () => !!props.message.workSessionUuid && (props.message.workSessionEntryUuids?.length ?? 0) > 0
)

const openDraft = () => {
  if (!props.message.workSessionUuid) return
  emit('openDraft', {
    workSessionUuid: props.message.workSessionUuid,
    entryId: props.message.workSessionEntryUuids?.[0]
  })
}

// Open the whole WorkSession in the full /editor page (not the side panel).
const openFullEditor = () => {
  if (!props.message.workSessionUuid) return
  navigateTo(Routes.EDITOR, {
    query: {
      uuid: props.message.workSessionUuid,
      entry: props.message.workSessionEntryUuids?.[0]
    }
  })
}

const showCitations = ref(false)
const contentRef = ref<HTMLElement | null>(null)
const tooltipRef = ref<HTMLDivElement | null>(null)
const tooltipArrowRef = ref<HTMLDivElement | null>(null)
const showTooltip = ref(false)
const tooltipText = ref('')
const feedback = ref<'like' | 'dislike' | null>(null)
const citationDialogOpen = ref(false)
const selectedCitation = ref<Citation | null>(null)

const isUser = computed(() => props.message.role === 'user')
const userStore = useUserStore()
const { translateKey } = useTranslateKey()
const canViewDebugSteps = computed(() => userStore.isAdmin)

// Playful rotating "thinking" verb shown while an assistant reply is generating.
const thinkingVerbs = computed(() => [
  translateKey('featureResearch.researchChat.thinking1'),
  translateKey('featureResearch.researchChat.thinking2'),
  translateKey('featureResearch.researchChat.thinking3'),
  translateKey('featureResearch.researchChat.thinking4'),
  translateKey('featureResearch.researchChat.thinking5')
])
const thinkingIndex = ref(0)
const thinkingVerb = computed(
  () => thinkingVerbs.value[thinkingIndex.value % thinkingVerbs.value.length]
)
let thinkingTimer: ReturnType<typeof setInterval> | null = null

const stopThinkingTimer = () => {
  if (thinkingTimer) {
    clearInterval(thinkingTimer)
    thinkingTimer = null
  }
}

watch(
  () => props.message.isLoading,
  loading => {
    if (loading) {
      if (thinkingTimer) return
      thinkingIndex.value = Math.floor(Math.random() * thinkingVerbs.value.length)
      thinkingTimer = setInterval(() => {
        thinkingIndex.value += 1
      }, 2500)
    } else {
      stopThinkingTimer()
    }
  },
  { immediate: true }
)

onUnmounted(stopThinkingTimer)

const { renderedHtml } = useMarkdown(
  () => props.message.content,
  () => props.message.citations
)

const isSafeCitationUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

const buildEditorContentWithCitationLinks = (): string => {
  const citations = props.message.citations
  if (!citations || citations.length === 0) {
    return renderedHtml.value
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(`<div>${renderedHtml.value}</div>`, 'text/html')
  const root = doc.body.firstElementChild
  if (!root) return renderedHtml.value

  const markers = root.querySelectorAll<HTMLSpanElement>(
    'span.citation-marker[data-citation-index]'
  )
  markers.forEach(marker => {
    const rawIndex = marker.dataset.citationIndex ?? ''
    const citationIndex = Number.parseInt(rawIndex, 10)
    if (!Number.isInteger(citationIndex) || citationIndex <= 0) return

    const citation = citations[citationIndex - 1]
    const citationUrl = citation?.url?.trim()
    if (!citationUrl || !isSafeCitationUrl(citationUrl)) return

    const anchor = doc.createElement('a')
    anchor.href = citationUrl
    anchor.target = '_blank'
    anchor.rel = 'noopener noreferrer'
    anchor.textContent = marker.textContent ?? `[${citationIndex}]`
    marker.replaceWith(anchor)
  })

  return root.innerHTML
}

const sendToEditor = () => {
  const editorContent = buildEditorContentWithCitationLinks()
  const prefillToken = storeEditorPrefill(editorContent)
  if (props.compact) {
    emit('sendToEditor', editorContent)
  } else {
    navigateTo(Routes.EDITOR, {
      query: prefillToken ? { prefillToken } : undefined,
      hash: prefillToken ? undefined : editorContent
    })
  }
}

const formattedTime = computed(() => {
  if (!props.message.creationTimestamp) return ''
  const date = new Date(props.message.creationTimestamp)
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
})

const hasCitations = computed(() => {
  return props.message.citations && props.message.citations.length > 0
})

const citationCount = computed(() => {
  return props.message.citations?.length || 0
})

const toggleCitations = () => {
  showCitations.value = !showCitations.value
}

const toggleFeedback = (value: 'like' | 'dislike') => {
  feedback.value = feedback.value === value ? null : value
  emit('feedback', {
    messageId: props.message.uuid,
    value: feedback.value
  })
}

const retryMessage = () => {
  emit('retry', props.message.uuid)
}

const openCitationDialog = (citation: Citation) => {
  if (!citation?.url) return
  selectedCitation.value = citation
  citationDialogOpen.value = true
}

const updateTooltipPosition = async (target: HTMLElement) => {
  if (!tooltipRef.value || !tooltipArrowRef.value) return

  const { x, y, middlewareData, placement } = await computePosition(target, tooltipRef.value, {
    placement: 'top',
    middleware: [
      offset(8),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: tooltipArrowRef.value })
    ]
  })

  Object.assign(tooltipRef.value.style, {
    left: `${x}px`,
    top: `${y}px`
  })

  if (middlewareData.arrow) {
    const { x: arrowX, y: arrowY } = middlewareData.arrow
    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right'
    }[placement.split('-')[0]]

    Object.assign(tooltipArrowRef.value.style, {
      left: arrowX !== null && arrowX !== undefined ? `${arrowX}px` : '',
      top: arrowY !== null && arrowY !== undefined ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide!]: '-4px'
    })
  }
}

const handleContentMouseEnter = async (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target.classList.contains('citation-marker')) {
    const title = target.getAttribute('title')
    if (title) {
      tooltipText.value = title
      showTooltip.value = true
      await nextTick()
      updateTooltipPosition(target)
    }
  }
}

const handleContentMouseLeave = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target.classList.contains('citation-marker')) {
    showTooltip.value = false
  }
}

const handleContentClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const anchor = target.closest('a[href]')
  if (anchor) {
    event.preventDefault()
    showTooltip.value = false
    const href = anchor.getAttribute('href')?.trim()
    if (href) {
      openCitationDialog({
        title: anchor.textContent?.trim() || 'External Link',
        url: href
      })
    }
    return
  }

  if (target.classList.contains('citation-marker')) {
    event.preventDefault()
    showTooltip.value = false
    const citationIndex = target.getAttribute('data-citation-index')
    if (citationIndex && props.message.citations) {
      const arrayIndex = parseInt(citationIndex, 10) - 1
      const citation = props.message.citations[arrayIndex]
      if (citation?.url) {
        openCitationDialog(citation)
      }
    }
  }
}
</script>

<template>
  <div class="font-mont-blanc-light flex w-full gap-3" :class="{ 'justify-end': isUser }">
    <div
      v-if="props.showAvatar"
      class="flex size-8 shrink-0 items-center justify-center rounded-full"
      :class="
        isUser
          ? 'from-primary to-primary/90 bg-gradient-to-br'
          : 'from-muted to-muted/80 border-muted-foreground/20 border bg-gradient-to-br'
      "
    ></div>

    <div
      class="flex max-w-[80%] flex-col gap-1"
      :class="{ 'items-end': isUser, 'ml-auto': isUser }"
    >
      <div v-if="message.isLoading" class="flex w-full flex-col gap-2 py-1">
        <div class="text-muted-foreground flex items-center gap-2 text-sm">
          <span class="thinking-dot" />
          <span class="thinking-shimmer">{{ thinkingVerb }}…</span>
        </div>
        <CookingPotEditorSkeleton :words="60" :minCh="3" :maxCh="10" />
      </div>
      <div v-else-if="isUser" class="border-rc-accent border-r-2 pr-4 text-right">
        <div
          class="font-panama-regular text-foreground text-lg leading-relaxed whitespace-pre-wrap"
        >
          {{ message.content }}
        </div>
      </div>
      <div
        v-else
        ref="contentRef"
        class="assistant-markdown prose font-panama-regular text-foreground max-w-none px-0 py-0 text-[17px] leading-[1.72]"
        v-html="renderedHtml"
        @click="handleContentClick"
        @mouseenter="handleContentMouseEnter"
        @mouseleave="handleContentMouseLeave"
      />

      <div v-if="!isUser && !message.isLoading" class="mt-2 flex flex-col gap-2">
        <button
          v-if="hasDraft"
          type="button"
          data-testid="research-chat-draft-artifact"
          class="border-border bg-card hover:border-primary/50 hover:bg-muted/50 group flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors"
          :class="props.draftOpen ? 'border-primary/60 bg-muted/40' : ''"
          @click="openDraft"
        >
          <div
            class="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg"
          >
            <FileText class="size-4" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="text-foreground truncate text-sm font-medium">
              {{ translateKey('researchChatDraftArtifactTitle') }}
            </div>
            <div class="text-muted-foreground truncate text-xs">
              {{
                props.draftOpen
                  ? translateKey('researchChatDraftArtifactClose')
                  : translateKey('researchChatDraftArtifactOpen')
              }}
            </div>
          </div>
          <ChevronDown
            class="text-muted-foreground size-4 shrink-0 transition-transform duration-200"
            :class="props.draftOpen ? '-rotate-90' : 'rotate-0'"
          />
        </button>

        <button
          v-if="hasDraft"
          type="button"
          data-testid="research-chat-draft-open-full"
          class="text-muted-foreground hover:text-foreground hover:bg-muted/40 -mt-1 flex w-fit items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors"
          @click="openFullEditor"
        >
          <ExternalLink class="size-3" />
          {{ translateKey('researchChatOpenFullEditor') }}
        </button>

        <AdminDebugStepsPanel
          v-if="canViewDebugSteps"
          :steps="message.debugSteps"
          title="Research Chat Debug"
        />

        <div class="flex flex-wrap gap-2">
          <Button
            v-if="props.showActions && hasCitations"
            variant="ghost"
            size="sm"
            class="text-muted-foreground hover:text-foreground hover:bg-accent/20 h-auto w-fit gap-2 px-2 py-1 transition-colors"
            @click="toggleCitations"
          >
            <FileText class="size-3" />
            <span class="text-xs">{{
              translateKey('featureResearch.researchChat.sourcesCount', {
                count: citationCount
              })
            }}</span>
            <ChevronDown
              class="size-3 transition-transform duration-200"
              :class="showCitations ? 'rotate-180' : ''"
            />
          </Button>

          <Button
            v-if="props.showActions && props.showSendToEditor"
            variant="ghost"
            size="sm"
            class="text-muted-foreground hover:text-foreground h-auto w-fit gap-2 px-2 py-1"
            @click="sendToEditor"
          >
            <Send class="size-3" />
            <span class="text-xs">{{
              translateKey('featureResearch.researchChat.sendToEditor')
            }}</span>
          </Button>

          <Button
            v-if="props.canRetry"
            variant="ghost"
            size="sm"
            class="text-muted-foreground hover:text-foreground h-auto w-fit gap-2 px-2 py-1"
            :disabled="props.retryDisabled"
            @click="retryMessage"
          >
            <RotateCcw class="size-3" />
            <span class="text-xs">{{ translateKey('featureResearch.researchChat.retry') }}</span>
          </Button>

          <Button
            v-if="props.showFeedback"
            variant="ghost"
            size="sm"
            class="text-muted-foreground hover:text-foreground h-auto w-fit gap-2 px-2 py-1"
            :class="
              feedback === 'like' ? 'text-rc-accent bg-rc-accent-soft hover:text-rc-accent' : ''
            "
            @click="toggleFeedback('like')"
          >
            <ThumbsUp class="size-3" />
            <span class="text-xs">{{ translateKey('featureResearch.researchChat.like') }}</span>
          </Button>

          <Button
            v-if="props.showFeedback"
            variant="ghost"
            size="sm"
            class="text-muted-foreground hover:text-foreground h-auto w-fit gap-2 px-2 py-1"
            :class="
              feedback === 'dislike' ? 'text-rc-accent bg-rc-accent-soft hover:text-rc-accent' : ''
            "
            @click="toggleFeedback('dislike')"
          >
            <ThumbsDown class="size-3" />
            <span class="text-xs">{{ translateKey('featureResearch.researchChat.dislike') }}</span>
          </Button>
        </div>

        <Transition name="citations">
          <div
            v-if="props.showActions && showCitations && hasCitations"
            class="mt-1 flex max-w-[62ch] flex-col"
          >
            <ResearchChatCitationCard
              v-for="(citation, index) in message.citations"
              :key="index"
              :citation="citation"
              :index="index + 1"
              @select="openCitationDialog"
            />
          </div>
        </Transition>
      </div>

      <span class="text-muted-foreground px-2 text-xs">{{ formattedTime }}</span>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="showTooltip" ref="tooltipRef" class="citation-tooltip" role="tooltip">
      {{ tooltipText }}
      <div ref="tooltipArrowRef" class="citation-tooltip-arrow" />
    </div>
  </Teleport>
  <ResearchCitationActionDialog v-model:open="citationDialogOpen" :citation="selectedCitation" />
</template>

<style scoped>
.thinking-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: hsl(var(--primary));
  animation: thinking-pulse 1.4s ease-in-out infinite;
}

.thinking-shimmer {
  background: linear-gradient(
    90deg,
    hsl(var(--muted-foreground)) 0%,
    hsl(var(--muted-foreground)) 35%,
    hsl(var(--foreground)) 50%,
    hsl(var(--muted-foreground)) 65%,
    hsl(var(--muted-foreground)) 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: thinking-shimmer 2s linear infinite;
}

@keyframes thinking-pulse {
  0%,
  100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes thinking-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .thinking-dot,
  .thinking-shimmer {
    animation: none;
  }
}

.citations-enter-active,
.citations-leave-active {
  transition: all 0.2s ease-out;
}
.citations-enter-from,
.citations-leave-to {
  opacity: 0;
  max-height: 0;
}
.citations-enter-to,
.citations-leave-from {
  opacity: 1;
  max-height: 500px;
}

:deep(.citation-marker) {
  display: inline-block;
  margin: 0 0.05rem;
  font-family: var(--font-mont-blanc-regular);
  font-size: 0.62rem;
  font-weight: 600;
  line-height: 1;
  vertical-align: super;
  color: var(--rc-accent);
  cursor: pointer;
  transition: color 0.15s ease;
}

:deep(.citation-marker:hover) {
  color: color-mix(in srgb, var(--rc-accent) 70%, var(--foreground));
}

:deep(.assistant-markdown h1),
:deep(.assistant-markdown h2),
:deep(.assistant-markdown h3) {
  margin-top: 1.35rem;
  margin-bottom: 0.65rem;
}

:deep(.assistant-markdown p) {
  margin-top: 0.5rem;
  margin-bottom: 0.6rem;
}

:deep(.assistant-markdown ul),
:deep(.assistant-markdown ol) {
  margin: 0.65rem 0;
}

:deep(.assistant-markdown pre) {
  margin: 1rem 0;
  border: 1px solid hsl(var(--border));
  border-radius: 0.9rem;
  background: hsl(var(--muted));
  padding: 1rem 1.15rem;
}

:deep(.assistant-markdown code:not(pre code)) {
  border-radius: 0.3rem;
  background: hsl(var(--muted));
  padding: 0.08rem 0.36rem;
}

:deep(.assistant-markdown blockquote) {
  border-left: 2px solid hsl(var(--primary));
  margin: 0.75rem 0;
  padding: 0.3rem 0 0.3rem 0.85rem;
  color: hsl(var(--muted-foreground));
}

:deep(.assistant-markdown a) {
  color: var(--rc-accent);
  text-decoration: underline;
  text-underline-offset: 0.2em;
  text-decoration-thickness: 1px;
  text-decoration-color: var(--rc-accent-soft);
}

:deep(.assistant-markdown a:hover) {
  text-decoration-color: var(--rc-accent);
}

:deep(.assistant-markdown blockquote) {
  border-left-color: var(--rc-accent);
}

:deep(table) {
  border-collapse: collapse;
  width: 100%;
  font-size: 0.875rem;
}

:deep(table th) {
  text-align: left;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border: 1px solid hsl(var(--border));
  background-color: hsl(var(--muted));
}

:deep(table td) {
  padding: 0.5rem 1rem;
  border: 1px solid hsl(var(--border));
}

:deep(table tbody tr:nth-child(even)) {
  background-color: hsl(var(--muted) / 0.5);
}

:deep(table tbody tr:nth-child(odd)) {
  background-color: hsl(var(--background));
}

:deep(hr) {
  margin: 1rem 0;
  border: 0;
  border-top: 1px solid hsl(var(--border));
}

.citation-tooltip {
  position: absolute;
  z-index: 50;
  max-width: 320px;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: hsl(var(--popover-foreground));
  background-color: hsl(var(--popover));
  border: 1px solid hsl(var(--border));
  border-radius: 0.5rem;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  animation: fadeIn 0.15s ease-out;
}

.citation-tooltip-arrow {
  position: absolute;
  width: 8px;
  height: 8px;
  background-color: hsl(var(--popover));
  border: 1px solid hsl(var(--border));
  transform: rotate(45deg);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
